/**
 * `<ActionButton>` — a monks-ui v4 `Button` with click feedback, used for
 * primary "Create / New" actions (MFINS-3092).
 *
 * Adds a material-style **ripple** emanating from the click point on top of
 * the plain `Button`. The previous hover-elevation effect (lift + shadow)
 * was removed: it read as unexpected motion on otherwise-static toolbars
 * and emptystates and was not part of the design system contract.
 *
 * It is a thin, drop-in wrapper: every `ButtonProps` (variant, size, asChild,
 * disabled, handlers, ref) is forwarded untouched, so existing call sites only
 * change the imported component name.
 *
 * Accessibility: when the user prefers reduced motion the ripple is never
 * spawned — the component degrades to a plain `Button` passthrough.
 *
 * Note: ripples require a positioned, clipped host, so `relative
 * overflow-hidden` is merged into the className. `asChild` consumers that need
 * a different overflow behavior should not use `ActionButton`.
 */
import { useCallback, useRef, useState } from 'react';
import type { PointerEvent as ReactPointerEvent } from 'react';
import { Button, type ButtonProps } from '@monksflow/monks-ui';
import { useReducedMotion } from 'motion/react';
import clsx from 'clsx';

interface Ripple {
  id: number;
  /** Top-left x of the ripple span, in button-local px. */
  x: number;
  /** Top-left y of the ripple span, in button-local px. */
  y: number;
  /** Diameter of the ripple span in px (square, made round via rounded-full). */
  size: number;
}

export type ActionButtonProps = ButtonProps;

export function ActionButton(props: ActionButtonProps) {
  const { className, onPointerDown, children, ...rest } = props;
  const shouldReduceMotion = useReducedMotion();

  const [ripples, setRipples] = useState<Ripple[]>([]);
  // Monotonic id so concurrent ripples (rapid clicks) get unique React keys.
  const nextRippleId = useRef(0);

  const spawnRipple = useCallback((event: ReactPointerEvent<HTMLButtonElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    // Cover the button regardless of where it's clicked: a circle whose
    // diameter equals the largest button edge, centered on the pointer.
    const size = Math.max(rect.width, rect.height);
    // Fall back to the button center when pointer coords are unavailable
    // (e.g. keyboard-triggered clicks, or jsdom synthetic events in tests)
    // so the ripple never gets a NaN position.
    const pointerX = Number.isFinite(event.clientX) ? event.clientX : rect.left + rect.width / 2;
    const pointerY = Number.isFinite(event.clientY) ? event.clientY : rect.top + rect.height / 2;
    const x = pointerX - rect.left - size / 2;
    const y = pointerY - rect.top - size / 2;
    const id = nextRippleId.current++;
    setRipples((current) => [...current, { id, x, y, size }]);
  }, []);

  const handlePointerDown = useCallback(
    (event: ReactPointerEvent<HTMLButtonElement>) => {
      if (!shouldReduceMotion && !rest.disabled) {
        spawnRipple(event);
      }
      onPointerDown?.(event);
    },
    [shouldReduceMotion, rest.disabled, spawnRipple, onPointerDown],
  );

  const removeRipple = useCallback((id: number) => {
    setRipples((current) => current.filter((ripple) => ripple.id !== id));
  }, []);

  if (shouldReduceMotion) {
    // Plain passthrough — no ripple host, no elevation, no extra children.
    return (
      <Button className={className} onPointerDown={onPointerDown} {...rest}>
        {children}
      </Button>
    );
  }

  return (
    <Button
      className={clsx('relative overflow-hidden', className)}
      onPointerDown={handlePointerDown}
      {...rest}
    >
      {children}
      {ripples.map((ripple) => (
        <span
          key={ripple.id}
          aria-hidden="true"
          data-testid="action-button-ripple"
          className="pointer-events-none absolute rounded-full bg-current animate-ripple"
          style={{
            left: ripple.x,
            top: ripple.y,
            width: ripple.size,
            height: ripple.size,
          }}
          onAnimationEnd={() => removeRipple(ripple.id)}
        />
      ))}
    </Button>
  );
}

ActionButton.displayName = 'ActionButton';
